import { useCallback, useEffect, useRef, useState } from 'react';
import { type Result } from '@vello/core';

export type ReadState<T> = { readonly status: 'loading' } | { readonly status: 'error'; readonly code: string } | { readonly status: 'ready'; readonly value: T };

/**
 * One read of a dashboard (D513): `loading` until it answers, then `ready` with the value or `error` with the server's code.
 * `read` is called again whenever `deps` change or `reload()` is called; a late answer of an older call is dropped. A refused
 * read is an `error`, never a quiet zero — the panel says it could not load and offers a retry. Pass `undefined` to hold the
 * read back (a screen waiting for a market): the state is then `undefined`.
 */
export function useRead<T>(read: (() => Promise<Result<T, { readonly code: string }>>) | undefined, deps: readonly unknown[]): { state: ReadState<T> | undefined; reload: () => void } {
  const [state, setState] = useState<ReadState<T> | undefined>(read === undefined ? undefined : { status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const seq = useRef(0);
  const readRef = useRef(read);
  readRef.current = read;
  const enabled = read !== undefined;

  useEffect(() => {
    const fn = readRef.current;
    if (fn === undefined) {
      setState(undefined);
      return undefined;
    }
    const mine = ++seq.current;
    setState({ status: 'loading' });
    void fn().then((res) => {
      if (mine !== seq.current) return;
      setState(res.ok ? { status: 'ready', value: res.value } : { status: 'error', code: res.error.code });
    });
    return () => {
      seq.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `deps` is the caller's list; `read` is read through a ref
  }, [enabled, attempt, ...deps]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  return { state, reload };
}
